/**
 * Real WebTorrent P2P - Using CDN
 */

import { useState, useEffect, useRef } from 'react';
import { getFile, saveFile } from './fileStorage';
import { parseMagnetURI } from './sanitize';
import { getVideo } from './metadata';
import type { VideoMetadata } from './metadata';

let torrentClient: any = null;
const activeTorrents = new Map<string, any>();

export function initWebTorrent(): any {
  if (torrentClient) return torrentClient;

  try {
    // Check if WebTorrent loaded from CDN
    if ((window as any).WebTorrent) {
      torrentClient = new (window as any).WebTorrent({
        tracker: {
          announce: [
            'wss://tracker.openwebtorrent.com',
            'wss://tracker.webtorrent.io'
          ]
        },
        maxConns: 55
      });

      console.log('✅ WebTorrent initialized (real)');
      return torrentClient;
    }

    // Fallback to mock if CDN not available
    console.log('⚠️ WebTorrent CDN not loaded, using mock');
    return getMockClient();
  } catch (err) {
    console.error('WebTorrent error:', err);
    return getMockClient();
  }
}

function getMockClient() {
  return {
    seed: (files: any, cb: any) => {
      const file = Array.isArray(files) ? files[0] : files;
      const infoHash = 'mock-' + Math.random().toString(36).slice(2, 11);
      console.log(`[MOCK] Seeding: ${file.name || file}`);
      cb({
        infoHash,
        magnetURI: `magnet:?xt=urn:btih:${infoHash}`,
        numPeers: 1,
        progress: 1,
        destroy: () => {}
      });
    },
    add: (uri: string, cb: any) => {
      const hash = uri.split('btih:')[1]?.split('&')[0] || 'unknown';
      console.log(`[MOCK] Downloading: ${hash}`);
      cb({
        infoHash: hash,
        progress: 1,
        numPeers: 1,
        downloadSpeed: 1024000,
        uploadSpeed: 512000,
        on: () => {},
        destroy: () => {}
      });
    }
  };
}

export async function seedFile(file: File): Promise<{
  infoHash: string;
  magnetURI: string;
}> {
  const client = torrentClient || initWebTorrent();

  return new Promise((resolve) => {
    client.seed(file, (torrent: any) => {
      activeTorrents.set(torrent.infoHash, torrent);
      console.log(`Seeding: ${file.name}`);
      console.log(`Info Hash: ${torrent.infoHash}`);
      console.log(`Magnet URI: ${torrent.magnetURI}`);

      resolve({
        infoHash: torrent.infoHash,
        magnetURI: torrent.magnetURI
      });
    });
  });
}

export async function downloadTorrent(magnetURI: string): Promise<any> {
  const parsed = parseMagnetURI(magnetURI);
  if (!parsed) {
    console.warn('downloadTorrent: invalid magnet URI, ignoring');
    return null;
  }

  const client = torrentClient || initWebTorrent();

  return new Promise((resolve) => {
    client.add(parsed.raw, (torrent: any) => {
      activeTorrents.set(torrent.infoHash, torrent);
      console.log(`Downloading: ${torrent.infoHash}`);
      resolve(torrent);
    });
  });
}

export function getActiveTorrentFile(infoHash: string): any | null {
  const torrent = activeTorrents.get(infoHash);
  return torrent?.files?.[0] ?? null;
}

export function getActiveTorrents() {
  return Array.from(activeTorrents.values()).map(t => ({
    infoHash: t.infoHash,
    progress: t.progress || 1,
    numPeers: t.numPeers || 0
  }));
}

export function getWebTorrentClient(): any {
  return torrentClient || initWebTorrent();
}

export function destroyWebTorrent(): void {
  if (torrentClient) {
    activeTorrents.forEach(t => t.destroy?.());
    activeTorrents.clear();
    torrentClient = null;
    console.log('WebTorrent destroyed');
  }
}

export async function reseedFromMagnetURIs(magnetURIs: string[]): Promise<void> {
  const client = initWebTorrent();
  for (const magnetURI of magnetURIs) {
    const parsed = parseMagnetURI(magnetURI);
    if (!parsed) continue;
    if (activeTorrents.has(parsed.infoHash)) continue;
    if (client.get(magnetURI)) continue;
    const blob = await getFile(parsed.infoHash);
    if (!blob) continue;
    const name = parsed.displayName || parsed.infoHash;
    const file = new File([blob], name, { type: blob.type || 'application/octet-stream' });
    client.seed(file, (torrent: any) => {
      activeTorrents.set(torrent.infoHash, torrent);
    });
  }
}

export async function reseedCachedVideos(videos: VideoMetadata[]): Promise<void> {
  const client = initWebTorrent();
  for (const video of videos) {
    if (!video.magnetURI) continue;
    if (activeTorrents.has(video.hash)) continue;
    if (client.get(video.magnetURI)) continue;
    const blob = await getFile(video.hash);
    if (!blob) continue;
    const file = new File([blob], `${video.hash}.mp4`, { type: blob.type || 'video/mp4' });
    client.seed(file, (torrent: any) => {
      activeTorrents.set(torrent.infoHash, torrent);
    });
  }
}

export function useVideoStream(hash: string | null) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [streamingViaP2P, setStreamingViaP2P] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<number | null>(null);

  useEffect(() => {
    setVideoUrl(null);
    setStreamingViaP2P(false);
    setDownloadProgress(null);
    if (!hash) return;

    let cancelled = false;

    (async () => {
      const v = getVideo(hash);
      if (!v) return;
      try {
        const blob = await getFile(hash);
        if (cancelled) return;
        if (blob) {
          setVideoUrl(URL.createObjectURL(blob));
          return;
        }
        if (!v.magnetURI) return;
        setStreamingViaP2P(true);
        setDownloadProgress(0);
        const client = getWebTorrentClient();
        const attachTorrent = (torrent: any) => {
          if (cancelled || !videoRef.current) return;
          if (!torrent.files || !torrent.files.length) return;
          const file = torrent.files[0];
          if (typeof file.renderTo === 'function') file.renderTo(videoRef.current);
          else if (typeof file.streamTo === 'function') file.streamTo(videoRef.current);
          else if (typeof file.getBlobURL === 'function') {
            file.getBlobURL((err: any, url: string) => {
              if (!err && url && videoRef.current) videoRef.current.src = url;
            });
          }
          if (torrent.done) return;
          const progressInterval = setInterval(() => {
            setDownloadProgress(Math.round(torrent.progress * 100));
          }, 500);
          torrent.on('done', () => {
            clearInterval(progressInterval);
            setDownloadProgress(100);
            if (typeof file.getBlob === 'function') {
              file.getBlob((err: any, b: Blob) => {
                if (!err && b) saveFile(v.hash, b, v.title || v.hash);
              });
            }
          });
        };
        const existing = client.get(v.magnetURI);
        if (existing) attachTorrent(existing);
        else client.add(v.magnetURI, attachTorrent);
      } catch (error) {
        console.error('Failed to load video stream:', error);
      }
    })();

    return () => { cancelled = true; };
  }, [hash]);

  return { videoRef, videoUrl, streamingViaP2P, downloadProgress };
}
